import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "agent-selector"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "material-icons" }
const _hoisted_4 = { class: "agent-bubble-label" }
const _hoisted_5 = { class: "agent-bubble-hint" }
const _hoisted_6 = ["title"]
const _hoisted_7 = { class: "material-icons fab-icon" }

import { ref, onMounted, onBeforeUnmount } from 'vue';
import AgentChat from './AgentChat.vue';
import { useAgentStore } from '../stores/agent.store';
import type { AgentDefinition } from '../stores/agent.store';

const STORAGE_KEY = 'agent_fab_pos';

export default /*@__PURE__*/_defineComponent({
  __name: 'AgentFab',
  setup(__props) {

const store = useAgentStore();
const selectorOpen = ref(false);

// --- Drag state ---
const fabWrapper = ref<HTMLElement | null>(null);
const pos = ref({ right: 28, bottom: 28 });
let dragging = false;
let dragStartX = 0;
let dragStartY = 0;
let startRight = 28;
let startBottom = 28;
let moved = false;

function loadPos() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const p = JSON.parse(saved);
      pos.value = {
        right:  Math.max(8, Math.min(p.right,  window.innerWidth  - 68)),
        bottom: Math.max(8, Math.min(p.bottom, window.innerHeight - 68)),
      };
    }
  } catch {}
}

function savePos() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pos.value));
}

function onMouseDown(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('.agent-selector')) return;
  if ((e.target as HTMLElement).closest('.agent-chat-panel')) return;
  dragging = true;
  moved = false;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  startRight  = pos.value.right;
  startBottom = pos.value.bottom;
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup',   onMouseUp);
  e.preventDefault();
}

function onMouseMove(e: MouseEvent) {
  if (!dragging) return;
  const dx = e.clientX - dragStartX;
  const dy = e.clientY - dragStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
  pos.value = {
    right:  Math.max(8, Math.min(startRight  - dx, window.innerWidth  - 68)),
    bottom: Math.max(8, Math.min(startBottom - dy, window.innerHeight - 68)),
  };
}

function onMouseUp() {
  dragging = false;
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup',   onMouseUp);
  if (moved) savePos();
}

// Touch support
function onTouchStart(e: TouchEvent) {
  if ((e.target as HTMLElement).closest('.agent-selector')) return;
  if ((e.target as HTMLElement).closest('.agent-chat-panel')) return;
  const t = e.touches[0];
  dragging = true;
  moved = false;
  dragStartX = t.clientX;
  dragStartY = t.clientY;
  startRight  = pos.value.right;
  startBottom = pos.value.bottom;
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend',  onTouchEnd);
}

function onTouchMove(e: TouchEvent) {
  if (!dragging) return;
  e.preventDefault();
  const t = e.touches[0];
  const dx = t.clientX - dragStartX;
  const dy = t.clientY - dragStartY;
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
  pos.value = {
    right:  Math.max(8, Math.min(startRight  - dx, window.innerWidth  - 68)),
    bottom: Math.max(8, Math.min(startBottom - dy, window.innerHeight - 68)),
  };
}

function onTouchEnd() {
  dragging = false;
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend',  onTouchEnd);
  if (moved) savePos();
}

onMounted(loadPos);
onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('mouseup',   onMouseUp);
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend',  onTouchEnd);
});

// --- Agent logic ---
function toggleSelector() {
  if (moved) { moved = false; return; }
  if (store.selectedAgent) { store.clearAgent(); return; }
  selectorOpen.value = !selectorOpen.value;
}

function pickAgent(agent: AgentDefinition) {
  store.selectAgent(agent);
  selectorOpen.value = false;
}

function bubbleStyle(index: number) {
  const total = store.agentes.length;
  const radius = 120;
  const startAngle = 210;
  const endAngle   = 330;
  const step  = total > 1 ? (endAngle - startAngle) / (total - 1) : 0;
  const angle = (startAngle + index * step) * (Math.PI / 180);
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;
  return {
    transform: `translate(${x}px, ${y}px)`,
    transitionDelay: `${index * 0.05}s`,
  };
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "fabWrapper",
    ref: fabWrapper,
    class: "agent-fab-wrapper",
    style: _normalizeStyle({ right: pos.value.right + 'px', bottom: pos.value.bottom + 'px' }),
    onMousedown: onMouseDown,
    onTouchstartPassive: onTouchStart
  }, [
    (_unref(store).selectedAgent)
      ? (_openBlock(), _createBlock(AgentChat, { key: 0 }))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "fade-scale" }, {
      default: _withCtx(() => [
        (selectorOpen.value && !_unref(store).selectedAgent)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).agentes, (agent, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: agent.key,
                  class: "agent-bubble",
                  style: _normalizeStyle(bubbleStyle(i)),
                  onClick: ($event: any) => (pickAgent(agent))
                }, [
                  _createElementVNode("div", {
                    class: "agent-bubble-inner",
                    style: _normalizeStyle({ background: agent.color })
                  }, [
                    _createElementVNode("span", _hoisted_3, _toDisplayString(agent.icono), 1),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(agent.nombre), 1)
                  ], 4),
                  _createElementVNode("div", _hoisted_5, _toDisplayString(agent.nombre) + " · " + _toDisplayString(agent.descripcion), 1)
                ], 12, _hoisted_2))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", {
      class: _normalizeClass(["agent-fab", { 'fab-open': selectorOpen.value }]),
      onClick: toggleSelector,
      title: selectorOpen.value ? 'Cerrar' : 'Asistentes IA'
    }, [
      _createElementVNode("span", _hoisted_7, _toDisplayString(selectorOpen.value ? 'close' : 'smart_toy'), 1)
    ], 10, _hoisted_6)
  ], 36))
}
}

})